export const FLEX_STATUSES = Object.freeze([
    "draft",
    "submitted",
    // Review-цикл (добавлен 2026-05-29): первый GET карточки админом
    // авто-переключает submitted → in_review (org-wide latch). Из in_review
    // админ жмёт «Принять в работу» (→ approved) или «Отклонить» (→ rejected).
    "in_review",
    "approved",
    "pickup",
    // Готовится к отправке (2026-05-29): после того как места измерены и
    // итоговая сумма объявлена, заказ комплектуется к отправке — печать
    // наклеек по местам и т.п. Стоит между «Прибыло на склад» и «Таможня».
    "preparing",
    "customs",
    "in_transit",
    "assigned",
    "delivery",
    "delivered",
    // Терминальный в review-графе. Юзер видит причину и через
    // PATCH /parcel-requests/:id/resubmit откатывает заказ в submitted.
    "rejected",
]);
// Терминальные статусы — заявка завершена и относится к «истории», не к
// «активным» (потребитель: клиентский GET /orders?status=…). Нюанс: rejected
// возвращаем (rejected → submitted через «Отправить повторно»), после
// resubmit заявка снова станет активной.
export const FLEX_TERMINAL_STATUSES = Object.freeze(["delivered", "rejected"]);
export const FLEX_STATUS_LABELS_RU = {
    draft: "Черновик",
    submitted: "Отправлена",
    in_review: "На рассмотрении",
    // Admin promotes to `approved` after reviewing the user's submission
    // (verifying form fields, photos, declared value). Pickup work only
    // starts after this gate.
    approved: "Принят в работу",
    // Внутренний код статуса остаётся `pickup`, но отдельный «забор курьером»
    // пока не реализован: после «Принят в работу» заказ физически приезжает на
    // склад, поэтому ярлык — «Прибыло на склад». Параметры мест (вес, габариты,
    // обрешётка) админ заполняет, как правило, именно на этом статусе.
    pickup: "Прибыло на склад",
    preparing: "Готовится к отправке",
    customs: "Таможня",
    in_transit: "В пути",
    assigned: "Назначен",
    delivery: "Доставка",
    delivered: "Доставлено",
    rejected: "Отклонено",
};
/** Volumetric-weight divisor: L×W×H(cm) / 5000 = kg. */
export const FLEX_VOLUMETRIC_DIVISOR = 5000;
// The admin announces an «Итого» for the deal: предварительная (may change) or
// итоговая (final). Drives the colour of the top sum panel and the bot message.
export const FLEX_ANNOUNCED_KINDS = ["preliminary", "final"];
export const FLEX_ANNOUNCED_KIND_LABELS_RU = {
    preliminary: "Предварительная",
    final: "Итоговая",
};
export const FLEX_TARIFF_CATEGORIES = ["personal", "commercial"];
export const FLEX_TARIFF_CATEGORY_LABELS_RU = {
    personal: "Личные вещи",
    commercial: "Коммерция",
};
// Страны, чья таможня различает тип отправки «Личные вещи» vs «Коммерция»
// (направления в/из РФ, Казахстана, Беларуси). Актуальный коридор
// Грузия → Европа этого не подразумевает — там тариф всегда «Личные вещи»,
// поэтому тумблер типа в админке скрыт (см. flexSupportsShipmentType). Когда
// запустим РФ/Казахстан — просто расширяем этот набор.
export const FLEX_SHIPMENT_TYPE_COUNTRIES = new Set([
    "RU",
    "KZ",
    "BY",
]);
/**
 * Подразумевает ли маршрут выбор типа отправки (Личные вещи/Коммерция)?
 * true, если отправление идёт В или ИЗ страны из FLEX_SHIPMENT_TYPE_COUNTRIES.
 */
export function flexSupportsShipmentType(originCountry, destinationCountry) {
    const o = (originCountry ?? "").toUpperCase();
    const d = (destinationCountry ?? "").toUpperCase();
    return FLEX_SHIPMENT_TYPE_COUNTRIES.has(o) || FLEX_SHIPMENT_TYPE_COUNTRIES.has(d);
}
/** €/кг by destination country code (Личные вещи, Грузия → Европа). */
export const FLEX_EUR_PER_KG = {
    AT: 7, BE: 7, HU: 7, DE: 7, GR: 7, DK: 7, ES: 7, IT: 7, CY: 7, LV: 7, LT: 7,
    LU: 7, MT: 7, PL: 7, PT: 7, RO: 7, SK: 7, SI: 7, FI: 7, FR: 7, HR: 7, CZ: 7,
    SE: 7, EE: 7, BG: 7,
    IE: 9, NL: 9,
    UA: 8,
    // RS (Сербия) is city-dependent — see flexSerbiaRate.
};
export const FLEX_DOOR_SURCHARGE_EUR = 20;
export const FLEX_DOOR_MAX_KG = 20; // «+20 € до двери» when billed weight < 20 kg
export const FLEX_BATUMI_SURCHARGE_GEL = 20;
function flexNormalizeCity(city) {
    return (city ?? "").toLowerCase().replace(/[\s\-–—]/g, "");
}
// Serbia: base 8 €/kg + city surcharge. Returns null for «other cities»
// (уточнять — no auto-calc).
const FLEX_SERBIA_CITIES = [
    { aliases: ["белград", "beograd", "belgrade"], surchargeEur: 0 },
    { aliases: ["новисад", "novisad"], surchargeEur: 50 },
    { aliases: ["суботица", "subotica"], surchargeEur: 100 },
];
function flexSerbiaRate(city) {
    const c = flexNormalizeCity(city);
    if (!c)
        return null;
    for (const entry of FLEX_SERBIA_CITIES) {
        if (entry.aliases.some((a) => c.includes(a)))
            return { eurPerKg: 8, surchargeEur: entry.surchargeEur };
    }
    return null;
}
export function flexIsBatumi(city) {
    const c = flexNormalizeCity(city);
    return c.includes("батуми") || c.includes("batumi");
}
/**
 * Pure auto-quote (no FX). Returns EUR-native total + GEL-native extra, or a
 * reason why it can't be computed. The caller converts EUR→GEL via the NBG
 * rate and rounds the FINAL lari sum (see flexRoundGelFinal).
 */
export function computeFlexQuote(input) {
    if (input.category !== "personal")
        return { ok: false, reason: "commercial" };
    const w = input.weightKg;
    if (w == null || !(w > 0))
        return { ok: false, reason: "no_weight" };
    // Тариф — по «иностранной» (не-грузинской) стороне маршрута, симметрично в
    // обе стороны (Грузия→Европа и Европа→Грузия). Города для надбавок:
    //  - foreignCity  — город на иностранной стороне (надбавки Сербии);
    //  - georgianCity — грузинский город (надбавка «До Батуми»), забор ИЛИ доставка.
    const GE = "GE";
    const origin = (input.originCountry ?? "").toUpperCase();
    const dest = (input.destinationCountry ?? "").toUpperCase();
    let code;
    let foreignCity;
    let georgianCity;
    if (dest && dest !== GE) {
        // …→Европа (вкл. legacy-вызовы без origin): иностранная сторона = назначение.
        code = dest;
        foreignCity = input.destinationCity;
        georgianCity = input.pickupCity;
    }
    else if (origin && origin !== GE) {
        // Европа→Грузия: иностранная сторона = отправление, грузинская = доставка.
        code = origin;
        foreignCity = input.pickupCity;
        georgianCity = input.destinationCity;
    }
    else {
        // Ни одна сторона не иностранная (напр. GE→GE) — авто-тариф неприменим.
        code = dest;
        foreignCity = input.destinationCity;
        georgianCity = input.pickupCity;
    }
    let eurPerKg;
    let serbiaSurcharge = 0;
    if (code === "RS") {
        const s = flexSerbiaRate(foreignCity);
        if (!s)
            return { ok: false, reason: "serbia_other" };
        eurPerKg = s.eurPerKg;
        serbiaSurcharge = s.surchargeEur;
    }
    else {
        const r = FLEX_EUR_PER_KG[code];
        if (r == null)
            return { ok: false, reason: "unknown_country" };
        eurPerKg = r;
    }
    const weightBilledKg = Math.ceil(w); // округление веса вверх
    const lines = [];
    const perKg = eurPerKg * weightBilledKg;
    lines.push({ label: `${eurPerKg} €/кг × ${weightBilledKg} кг`, amount: perKg, currency: "EUR" });
    if (serbiaSurcharge > 0)
        lines.push({ label: "Сербия (надбавка)", amount: serbiaSurcharge, currency: "EUR" });
    const door = weightBilledKg < FLEX_DOOR_MAX_KG ? FLEX_DOOR_SURCHARGE_EUR : 0;
    if (door > 0)
        lines.push({ label: "До двери (<20 кг)", amount: door, currency: "EUR" });
    const eurTotal = perKg + serbiaSurcharge + door;
    const gelExtra = flexIsBatumi(georgianCity) ? FLEX_BATUMI_SURCHARGE_GEL : 0;
    if (gelExtra > 0)
        lines.push({ label: "До Батуми", amount: gelExtra, currency: "GEL" });
    return { ok: true, weightBilledKg, eurPerKg, eurTotal, gelExtra, lines };
}
/** Round a lari amount UP to the nearest 5 — applied to the FINAL «Итого» only. */
export function flexRoundGelFinal(amountGel) {
    return Math.ceil(amountGel / 5) * 5;
}
/** Volumetric weight in kg: L×W×H / 5000. Null if any side is missing. */
export function flexVolumetricWeightKg(l, w, h) {
    if (l == null || w == null || h == null)
        return null;
    return (l * w * h) / FLEX_VOLUMETRIC_DIVISOR;
}
/** Billable weight of one place: the greater of actual and volumetric weight. */
export function flexPlaceBillableWeightKg(p) {
    const actual = p.weightKg ?? 0;
    const volumetric = flexVolumetricWeightKg(p.lengthCm, p.widthCm, p.heightCm) ?? 0;
    return Math.max(actual, volumetric);
}
/**
 * Final weight = Σ over places of max(actual, volumetric). Drives the FINAL
 * sum (the preliminary sum uses the manually-entered weight). Null when there
 * are no places or they sum to zero.
 */
export function flexFinalWeightKg(places) {
    if (!Array.isArray(places) || places.length === 0)
        return null;
    const sum = places.reduce((acc, p) => acc + flexPlaceBillableWeightKg(p), 0);
    return sum > 0 ? sum : null;
}
/**
 * A place is "complete" — measured enough to let the order advance in status.
 * General rule: weight is enough (dimensions are optional). But a crated place
 * (обрешётка) must also have all three dimensions. Used by the «Довоз» gate.
 */
export function flexPlaceComplete(p) {
    if (p.weightKg == null)
        return false;
    if (p.crateNeeded) {
        return p.lengthCm != null && p.widthCm != null && p.heightCm != null;
    }
    return true;
}
// Forward transitions per status. Review-цикл (2026-05-29):
//  submitted → in_review (auto, GET-latch)
//  in_review → approved (admin: Принять в работу) | rejected (admin: Отклонить)
//  rejected → submitted (юзер: Отправить повторно)
// Backward step по основной цепи (approved → pickup → … → delivered)
// разрешён в transitionFlexStatus отдельно, не через FLOW.
export const FLEX_STATUS_FLOW = {
    draft: ["submitted"],
    submitted: ["in_review"],
    in_review: ["approved", "rejected"],
    approved: ["pickup"],
    pickup: ["preparing"],
    preparing: ["customs"],
    customs: ["in_transit"],
    in_transit: ["assigned"],
    assigned: ["delivery"],
    delivery: ["delivered"],
    delivered: [],
    rejected: ["submitted"],
};
// ── Прод-граница: логистика после «Прибыло на склад» отложена ───────────────
// Контракт «статус ↔ AMO» (2026-05-30): граница ответственности = `pickup`.
// До pickup включительно жизненный цикл ведёт наш апп; после — заказ живёт по
// существующему процессу прод-воронки AMO (двигают люди), у нас эта цепочка не
// разработана. Поэтому forward за `pickup` в проде закрыт. Значения статусов
// остаются в enum под будущую «спящую» само-логистику; включается флагом
// FLEX_LOGISTICS_ENABLED (server: config; admin: VITE_FLEX_LOGISTICS_ENABLED).
/** Прод-терминал жизненного цикла Flex. Дальше — зона AMO-процесса. */
export const FLEX_STATUS_PROD_TERMINAL = "pickup";
/**
 * Статусы пост-`pickup` логистики (забор/таможня/перевозка/доставка). В проде
 * недостижимы: переход в них закрыт, пока логистика выключена. Деньги тут ни
 * при чём — оплата (`paidAt`) и суммы — отдельная ось, статус они не двигают.
 */
export const FLEX_LOGISTICS_STATUSES = Object.freeze([
    "preparing",
    "customs",
    "in_transit",
    "assigned",
    "delivery",
    "delivered",
]);
/** Активные в проде статусы (для UI-фильтров и степпера). */
export const FLEX_STATUS_PROD_ACTIVE = Object.freeze(FLEX_STATUSES.filter((s) => !FLEX_LOGISTICS_STATUSES.includes(s)));
/**
 * Разрешён ли forward-переход `current → next` с учётом гейта логистики.
 * Проверяет, что переход вообще есть в FLEX_STATUS_FLOW, и — когда логистика
 * выключена — запрещает вход в пост-`pickup` logistics-статусы. Review- и
 * ранние переходы (draft..approved→pickup) не затрагиваются.
 */
export function flexForwardAllowed(current, next, logisticsEnabled) {
    if (!(FLEX_STATUS_FLOW[current] ?? []).includes(next))
        return false;
    if (logisticsEnabled)
        return true;
    return !FLEX_LOGISTICS_STATUSES.includes(next);
}
//# sourceMappingURL=index.js.map